import * as AccordionPrimitive from "@radix-ui/react-accordion";
import Icon from "@/components/icon";
import Section from "@/components/section";
import { RowDivider } from "@/components/row";
import { VitrinCategory } from "@/models/home/VitrinCategory";
import { Link } from "react-router-dom";
import { ELEMENT_TYPES } from "@/features/analytics";

interface CategoryListProps {
  categories: VitrinCategory[];
}

function CategoryIconTile({ id }: { id: number }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-primary/10">
      <Icon name={`category-${id}`} size={18} className="text-primary" />
    </span>
  );
}

export default function CategoryList2({ categories }: CategoryListProps) {
  if (categories.length === 0) {
    return (
      <div className="py-8 text-center text-default-600">
        دسته‌بندی‌ای یافت نشد
      </div>
    );
  }

  return (
    // Telegram-style grouped card: one white block, rows split by inset
    // dividers. Categories with children expand inline (single-open accordion).
    <Section>
      <AccordionPrimitive.Root
        type="single"
        collapsible
        data-track-section="category_list"
      >
        {categories.map((category, index) => {
          const hasChildren = Boolean(
            category.children && category.children.length > 0,
          );
          const divider = index > 0 ? <RowDivider inset /> : null;

          if (!hasChildren) {
            return (
              <div key={category.id}>
                {divider}
                <Link
                  to={`/categories/${category.id}`}
                  dir="rtl"
                  className="flex items-center gap-3 px-4 py-3 transition active:bg-content2"
                  data-track-type={ELEMENT_TYPES.categoryLink}
                  data-track-id={category.id}
                  data-track-slot={index}
                  data-track-target="category"
                >
                  <CategoryIconTile id={category.id} />
                  <span className="min-w-0 flex-1 text-sm text-default-900">
                    {category.name}
                  </span>
                  <Icon
                    name="chevron-left"
                    size={18}
                    className="text-default-300"
                  />
                </Link>
              </div>
            );
          }

          return (
            <AccordionPrimitive.Item
              key={category.id}
              value={String(category.id)}
            >
              {divider}
              <AccordionPrimitive.Header className="flex">
                {/* Expanding a group is not navigating to it — `target_type:
                    none` is what tells the two apart in the reports. */}
                <AccordionPrimitive.Trigger
                  dir="rtl"
                  className="group flex w-full items-center gap-3 px-4 py-2.5 text-right outline-none transition active:bg-content2"
                  data-track-type={ELEMENT_TYPES.categoryLink}
                  data-track-id={category.id}
                  data-track-slot={index}
                  data-track-target="none"
                >
                  <CategoryIconTile id={category.id} />
                  <span className="min-w-0 flex-1 text-sm text-default-900">
                    {category.name}
                  </span>
                  <Icon
                    name="chevron-left"
                    size={18}
                    className="text-default-300 transition-transform duration-200 group-data-[state=open]:-rotate-90"
                  />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>

              <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div className="border-t border-content3 bg-content2" dir="rtl">
                  <Link
                    to={`/categories/${category.id}`}
                    className="block py-3 pl-4 pr-[3.75rem] text-sm text-primary transition active:bg-content3"
                    data-track-type={ELEMENT_TYPES.categoryLink}
                    data-track-id={category.id}
                    data-track-target="category"
                  >
                    همه «{category.name}»
                  </Link>
                  {category.children!.map((child) => (
                    <Link
                      key={child.id}
                      to={`/categories/${child.id}`}
                      className="block py-3 pl-4 pr-[3.75rem] text-sm text-default-700 transition active:bg-content3"
                      data-track-type={ELEMENT_TYPES.categoryLink}
                      data-track-id={child.id}
                      data-track-target="category"
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          );
        })}
      </AccordionPrimitive.Root>
    </Section>
  );
}
